import { Controller } from '@hotwired/stimulus'

// Chrome/Edge fire `beforeinstallprompt` once the PWA is installable. We stash
// the event at module load (it fires on page load, long before the settings
// modal opens) so the install button can trigger the native dialog on demand.
let deferredInstallPrompt = null
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault()
  deferredInstallPrompt = event
})
window.addEventListener('appinstalled', () => {
  deferredInstallPrompt = null
})

// Manages Web Push opt-in/opt-out for the current browser.
//
//   <div data-controller="push-subscription">
//     <input type="checkbox" data-push-subscription-target="toggle"
//            data-action="push-subscription#toggle">
//     <span data-push-subscription-target="status"></span>
//   </div>
export default class extends Controller {
  static targets = ['toggle', 'status', 'control', 'install', 'installHint']
  // Every status and hint shown here — translated by the ERB that mounts
  // this controller (stimulus.push_subscription).
  static values = { messages: Object }

  async connect () {
    // On mobile, Web Push only works once the site is installed to the home
    // screen (a standalone PWA). Until then, show install instructions instead
    // of a toggle that can't possibly work.
    if (this.needsInstall) {
      this.showInstall()
      return
    }

    if (!this.supported) {
      this.disable(this.messagesValue.unsupported)
      return
    }

    this.registration = await navigator.serviceWorker.register('/service-worker')
    await this.refreshState()
  }

  async toggle (event) {
    if (event.target.checked) {
      await this.subscribe()
    } else {
      await this.unsubscribe()
    }
  }

  // Lets the user tap anywhere on the block, not just the checkbox.
  async toggleBlock (event) {
    if (this.needsInstall) return
    if (!this.hasToggleTarget || this.toggleTarget.disabled) return
    // The checkbox fires its own change->toggle, so ignore direct clicks on it.
    if (event.target === this.toggleTarget) return

    this.toggleTarget.checked = !this.toggleTarget.checked
    if (this.toggleTarget.checked) {
      await this.subscribe()
    } else {
      await this.unsubscribe()
    }
  }

  // Triggered by the "add to home screen" button in the install banner.
  async install (event) {
    // Don't let the click bubble up to toggleBlock on the wrapper.
    if (event) event.stopPropagation()

    // Android/Chromium: show the real OS install dialog.
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt()
      const { outcome } = await deferredInstallPrompt.userChoice
      deferredInstallPrompt = null
      if (outcome === 'accepted') {
        this.setInstallHint(this.messagesValue.installing)
      }
      return
    }

    // iOS (no install API) or not-yet-eligible browsers: fall back to steps.
    this.setInstallHint(this.isIos ? this.messagesValue.install_ios : this.messagesValue.install_android)
  }

  setInstallHint (text) {
    if (this.hasInstallHintTarget) this.installHintTarget.textContent = text
  }

  showInstall () {
    if (this.hasInstallTarget) this.installTarget.classList.remove('hidden')
    if (this.hasControlTarget) this.controlTarget.classList.add('hidden')
  }

  async subscribe () {
    if (!this.vapidPublicKey) {
      console.error('[push] missing vapid-public-key meta tag')
      this.setStatus(this.messagesValue.not_configured)
      if (this.hasToggleTarget) this.toggleTarget.checked = false
      return
    }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      this.setStatus(this.messagesValue.blocked)
      if (this.hasToggleTarget) this.toggleTarget.checked = false
      return
    }

    try {
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(this.vapidPublicKey)
      })

      const response = await this.post('/push_subscription', this.serialize(subscription))
      if (response.ok) {
        this.setStatus(this.messagesValue.enabled)
        if (this.hasToggleTarget) this.toggleTarget.checked = true
      } else {
        this.setStatus(this.messagesValue.failed)
        if (this.hasToggleTarget) this.toggleTarget.checked = false
      }
    } catch (error) {
      console.error('[push] subscribe failed', error)
      this.setStatus(this.messagesValue.failed)
      if (this.hasToggleTarget) this.toggleTarget.checked = false
    }
  }

  async unsubscribe () {
    const subscription = await this.registration.pushManager.getSubscription()
    if (subscription) {
      await this.post('/push_subscription', { endpoint: subscription.endpoint }, 'DELETE')
      await subscription.unsubscribe()
    }
    this.setStatus(this.messagesValue.disabled)
    if (this.hasToggleTarget) this.toggleTarget.checked = false
  }

  async refreshState () {
    const subscription = await this.registration.pushManager.getSubscription()
    const active = !!subscription && Notification.permission === 'granted'
    if (this.hasToggleTarget) this.toggleTarget.checked = active
    this.setStatus(active ? this.messagesValue.enabled : this.messagesValue.disabled)
  }

  // --- helpers ---

  serialize (subscription) {
    const json = subscription.toJSON()
    return {
      endpoint: subscription.endpoint,
      p256dh_key: json.keys.p256dh,
      auth_key: json.keys.auth
    }
  }

  post (url, payload, method = 'POST') {
    return fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken,
        Accept: 'application/json'
      },
      body: JSON.stringify({ push_subscription: payload })
    })
  }

  disable (message) {
    if (this.hasToggleTarget) {
      this.toggleTarget.checked = false
      this.toggleTarget.disabled = true
    }
    this.setStatus(message)
  }

  setStatus (text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  get supported () {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  }

  // True on a phone that is still running inside the browser (not installed).
  get needsInstall () {
    return this.isMobile && !this.standalone
  }

  get standalone () {
    return window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
  }

  get isIos () {
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent)
  }

  get isMobile () {
    return this.isIos || /android/i.test(window.navigator.userAgent)
  }

  get vapidPublicKey () {
    return document.querySelector('meta[name="vapid-public-key"]')?.content
  }

  get csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}

function urlBase64ToUint8Array (base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
